import React, { useEffect, useState } from 'react';
import { ref, onValue, remove, update } from 'firebase/database';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';

const HomeAdmin = () => {
  const navigate = useNavigate();
  const [adminName, setAdminName] = useState('');
  const [tours, setTours] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [messages, setMessages] = useState([]); // Added messages state
  const [activeTab, setActiveTab] = useState('tours');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentItemType, setCurrentItemType] = useState(''); // 'enquiry' or 'message'

  useEffect(() => {
    const storedName = localStorage.getItem('adminName');
    if (!storedName) {
      navigate('/Admin-home');
    } else {
      setAdminName(storedName);
    }

    // Load tours
    const toursRef = ref(db, 'tours');
    onValue(toursRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loaded = Object.entries(data).map(([id, value]) => ({
          id,
          ...value
        }));
        setTours(loaded.reverse());
      }
    });

    // Load enquiries
    const enquiriesRef = ref(db, 'enquiries');
    onValue(enquiriesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loaded = Object.entries(data).map(([id, value]) => ({
          id,
          ...value
        }));
        setEnquiries(loaded.reverse());
      }
    });

    // Load messages (from contact form)
    const messagesRef = ref(db, 'messages');
    onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loaded = Object.entries(data).map(([id, value]) => ({
          id,
          ...value
        }));
        setMessages(loaded.reverse());
      }
    });
  }, [navigate]);

  const handleDeleteTour = (id) => {
    if (window.confirm("Are you sure you want to delete this tour?")) {
      remove(ref(db, `tours/${id}`))
        .then(() => alert("Tour deleted successfully."));
    }
  };

  const confirmDeleteItem = (id, type) => {
    setItemToDelete(id);
    setCurrentItemType(type);
    setShowDeleteModal(true);
  };

  const handleDeleteItem = () => {
    if (itemToDelete) {
      const refPath = currentItemType === 'enquiry' 
        ? `enquiries/${itemToDelete}`
        : `messages/${itemToDelete}`;
      
      remove(ref(db, refPath))
        .then(() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        })
        .catch((error) => {
          console.error(`Error deleting ${currentItemType}:`, error);
          alert(`Failed to delete ${currentItemType}. Please try again.`);
        });
    }
  };

  const markAsRead = (id, type) => {
    const refPath = type === 'enquiry' 
      ? `enquiries/${id}`
      : `messages/${id}`;
    
    update(ref(db, refPath), { status: 'read' });
  };

  const viewFullItem = (item, type) => {
    setSelectedItem(item);
    setCurrentItemType(type);
    setShowDetailModal(true);
    if (item.status === 'unread') {
      markAsRead(item.id, type);
    }
  };

  return (
    <div style={styles.container}>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirm Deletion</h3>
            <p>Are you sure you want to delete this {currentItemType}?</p>
            <div style={styles.modalButtons}>
              <button 
                style={styles.cancelButton}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                style={styles.deleteButton}
                onClick={handleDeleteItem}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <div style={styles.modalOverlay}>
          <div style={styles.detailModal}>
            <div style={styles.detailModalHeader}>
              <h3 style={styles.detailModalTitle}>
                {currentItemType === 'enquiry' ? 'Enquiry' : 'Message'} Details
              </h3>
              <button 
                style={styles.closeButton}
                onClick={() => setShowDetailModal(false)}
              >
                &times;
              </button>
            </div>
            <div style={styles.detailModalContent}>
              <div style={styles.detailRow}>
                <strong>Name:</strong>
                <span>{selectedItem.name}</span>
              </div>
              <div style={styles.detailRow}>
                <strong>Email:</strong>
                <a href={`mailto:${selectedItem.email}`}>{selectedItem.email}</a>
              </div>
              <div style={styles.detailRow}>
                <strong>Phone:</strong>
                <a href={`tel:${selectedItem.phone}`}>{selectedItem.phone}</a>
              </div>
              
              {currentItemType === 'enquiry' && (
                <div style={styles.detailRow}>
                  <strong>Tour Type:</strong>
                  <span>{selectedItem.tourType}</span>
                </div>
              )}
              
              <div style={styles.detailRow}>
                <strong>Date:</strong>
                <span>{new Date(selectedItem.timestamp).toLocaleString()}</span>
              </div>
              <div style={styles.detailRow}>
                <strong>Status:</strong>
                <span style={selectedItem.status === 'unread' ? styles.unreadBadge : styles.readBadge}>
                  {selectedItem.status === 'unread' ? 'NEW' : 'READ'}
                </span>
              </div>
              <div style={{...styles.detailRow, flexDirection: 'column'}}>
                <strong>Message:</strong>
                <p style={styles.fullMessage}>{selectedItem.message}</p>
              </div>
            </div>
            <div style={styles.detailModalFooter}>
              <button 
                style={styles.deleteButton}
                onClick={() => {
                  setShowDetailModal(false);
                  confirmDeleteItem(selectedItem.id, currentItemType);
                }}
              >
                Delete {currentItemType === 'enquiry' ? 'Enquiry' : 'Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.header}>
        <h2>🌐 Welcome, <span style={{ color: '#ffeb3b' }}>{adminName}</span></h2>
        <div style={styles.headerControls}>
          <div style={styles.tabContainer}>
            <button 
              style={activeTab === 'tours' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('tours')}
            >
              Tours
            </button>
            <button 
              style={activeTab === 'enquiries' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('enquiries')}
            >
              Enquiries ({enquiries.filter(e => e.status === 'unread').length})
            </button>
            <button 
              style={activeTab === 'messages' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('messages')}
            >
              Messages ({messages.filter(m => m.status === 'unread').length})
            </button>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem('adminName');
              navigate('/');
            }}
            style={styles.logoutBtn}
          >
            🔓 Logout
          </button>
        </div>
      </div>

      {activeTab === 'tours' ? (
        <>
          <TourSection
            title="🏞️ Domestic Tours"
            data={tours.filter(t => t.category === 'domestic-tour')}
            handleDelete={handleDeleteTour}
          />
          <TourSection
            title="✈️ International Tours"
            data={tours.filter(t => t.category === 'international-tour')}
            handleDelete={handleDeleteTour}
          />
        </>
      ) : activeTab === 'enquiries' ? (
        <DataSection 
          data={enquiries}
          type="enquiry"
          title="📨 Customer Enquiries"
          confirmDeleteItem={confirmDeleteItem}
          viewFullItem={viewFullItem}
        />
      ) : (
        <DataSection 
          data={messages}
          type="message"
          title="📩 Contact Messages"
          confirmDeleteItem={confirmDeleteItem}
          viewFullItem={viewFullItem}
        />
      )}
    </div>
  );
};

const TourSection = ({ title, data, handleDelete }) => {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.grid}>
        {data.length === 0 ? (
          <p style={styles.noDataText}>No tours found.</p>
        ) : (
          data.map(tour => (
            <div key={tour.id} style={styles.card}>
              <img src={tour.image} alt={tour.title} style={styles.cardImage} />
              <div style={styles.cardBody}>
                <h4 style={styles.cardTitle}>{tour.title}</h4>
                <p style={styles.cardText}><strong>Duration:</strong> {tour.duration} days</p>
                <p style={styles.cardText}>{tour.description}</p>
                <div style={styles.buttonGroup}>
                  <button style={styles.editBtn} onClick={() => alert('Edit logic can be added.')}>✏️ Edit</button>
                  <button style={styles.deleteBtn} onClick={() => handleDelete(tour.id)}>🗑️ Delete</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const DataSection = ({ data, type, title, confirmDeleteItem, viewFullItem }) => {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.tableContainer}>
        <table style={styles.dataTable}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Status</th>
              <th style={styles.tableHeader}>Name</th>
              <th style={styles.tableHeader}>Email</th>
              <th style={styles.tableHeader}>Phone</th>
              {type === 'enquiry' && <th style={styles.tableHeader}>Tour Type</th>}
              <th style={styles.tableHeader}>Date</th>
              <th style={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={type === 'enquiry' ? 7 : 6} style={styles.noDataCell}>
                  No {type === 'enquiry' ? 'enquiries' : 'messages'} found
                </td>
              </tr>
            ) : (
              data.map(item => (
                <tr 
                  key={item.id} 
                  style={{
                    ...styles.tableRow,
                    backgroundColor: item.status === 'unread' ? '#f8f9fa' : 'white'
                  }}
                >
                  <td style={styles.tableCell}>
                    <span style={item.status === 'unread' ? styles.unreadBadge : styles.readBadge}>
                      {item.status === 'unread' ? 'NEW' : 'READ'}
                    </span>
                  </td>
                  <td style={styles.tableCell}>{item.name}</td>
                  <td style={styles.tableCell}>
                    <a href={`mailto:${item.email}`} style={styles.emailLink}>
                      {item.email}
                    </a>
                  </td>
                  <td style={styles.tableCell}>
                    <a href={`tel:${item.phone}`} style={styles.phoneLink}>
                      {item.phone}
                    </a>
                  </td>
                  {type === 'enquiry' && (
                    <td style={styles.tableCell}>
                      {item.tourType}
                    </td>
                  )}
                  <td style={styles.tableCell}>
                    {new Date(item.timestamp).toLocaleDateString()}
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      <button 
                        style={styles.viewButton}
                        onClick={() => viewFullItem(item, type)}
                      >
                        View
                      </button>
                      <button 
                        style={styles.deleteTableBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDeleteItem(item.id, type);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '30px 20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: 'linear-gradient(to right, #f1f9ff, #ffffff)',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    background: 'linear-gradient(to right, #1e3c72, #2a5298)',
    padding: '20px 30px',
    color: '#fff',
    borderRadius: '12px',
    boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
    marginBottom: '30px',
  },
  headerControls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px'
  },
  tabContainer: {
    display: 'flex',
    gap: '10px',
  },
  tab: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.3)',
    padding: '8px 16px',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '14px',
  },
  activeTab: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.5)',
    padding: '8px 16px',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  logoutBtn: {
    background: 'linear-gradient(to right, #ff416c, #ff4b2b)',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '30px',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
    }
  },
  section: {
    marginBottom: '40px',
    overflowX: 'auto'
  },
  sectionTitle: {
    fontSize: '22px',
    color: '#1e88e5',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '2px solid #e0e0e0'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s ease',
    ':hover': {
      transform: 'translateY(-5px)'
    }
  },
  cardImage: {
    width: '100%',
    height: '180px',
    objectFit: 'cover'
  },
  cardBody: {
    padding: '15px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  cardTitle: {
    margin: '0 0 10px 0',
    color: '#2c3e50'
  },
  cardText: {
    margin: '0 0 10px 0',
    color: '#34495e',
    lineHeight: '1.5'
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 'auto',
    gap: '10px'
  },
  editBtn: {
    backgroundColor: '#007bff',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1,
    ':hover': {
      backgroundColor: '#0069d9'
    }
  },
  deleteBtn: {
    backgroundColor: '#dc3545',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1,
    ':hover': {
      backgroundColor: '#c82333'
    }
  },
  tableContainer: {
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    backgroundColor: 'white'
  },
  dataTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#1e3c72',
    color: 'white',
    padding: '12px 15px',
    textAlign: 'left',
    fontWeight: '600',
    position: 'sticky',
    top: 0
  },
  tableRow: {
    borderBottom: '1px solid #e0e0e0',
    transition: 'background-color 0.2s ease',
  },
  tableCell: {
    padding: '12px 15px',
    verticalAlign: 'middle',
    color: '#34495e'
  },
  noDataCell: {
    padding: '20px',
    textAlign: 'center',
    color: '#7f8c8d'
  },
  noDataText: {
    textAlign: 'center',
    color: '#7f8c8d',
    fontSize: '16px'
  },
  unreadBadge: {
    background: '#3498db',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  readBadge: {
    background: '#95a5a6',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  emailLink: {
    color: '#3498db',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline'
    }
  },
  phoneLink: {
    color: '#2c3e50',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline'
    }
  },
  actionButtons: {
    display: 'flex',
    gap: '8px'
  },
  viewButton: {
    backgroundColor: '#17a2b8',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    ':hover': {
      backgroundColor: '#138496'
    }
  },
  deleteTableBtn: {
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    ':hover': {
      backgroundColor: '#c0392b'
    }
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '8px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalTitle: {
    marginTop: 0,
    color: '#2c3e50',
    fontSize: '20px'
  },
  modalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#5a6268'
    }
  },
  deleteButton: {
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#c0392b'
    }
  },
  detailModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  detailModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 1
  },
  detailModalTitle: {
    margin: 0,
    color: '#2c3e50',
    fontSize: '22px'
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#7f8c8d',
    ':hover': {
      color: '#2c3e50'
    }
  },
  detailModalContent: {
    padding: '20px'
  },
  detailRow: {
    display: 'flex',
    marginBottom: '15px',
    gap: '10px'
  },
  fullMessage: {
    margin: '10px 0 0 0',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    lineHeight: '1.6'
  },
  detailModalFooter: {
    padding: '15px 20px',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  '@media (max-width: 768px)': {
    container: {
      padding: '20px 15px'
    },
    header: {
      padding: '15px 20px'
    },
    sectionTitle: {
      fontSize: '20px'
    },
    tableHeader: {
      padding: '10px 12px',
      fontSize: '14px'
    },
    tableCell: {
      padding: '10px 12px',
      fontSize: '14px'
    },
    actionButtons: {
      flexDirection: 'column',
      gap: '5px'
    },
    viewButton: {
      padding: '4px 8px',
      fontSize: '12px'
    },
    deleteTableBtn: {
      padding: '4px 8px',
      fontSize: '12px'
    }
  },
  '@media (max-width: 576px)': {
    container: {
      padding: '15px 10px'
    },
    header: {
      padding: '12px 15px'
    },
    sectionTitle: {
      fontSize: '18px'
    },
    tab: {
      padding: '6px 12px',
      fontSize: '12px'
    },
    activeTab: {
      padding: '6px 12px',
      fontSize: '12px'
    },
    logoutBtn: {
      padding: '8px 15px',
      fontSize: '12px'
    },
    dataTable: {
      display: 'block',
      overflowX: 'auto'
    },
    modal: {
      padding: '15px'
    },
    detailModalHeader: {
      padding: '15px'
    },
    detailModalContent: {
      padding: '15px'
    }
  }
};

export default HomeAdmin;